class ReviewsList extends HTMLElement {
  connectedCallback() {
    this.isDown = false;
    this.dragged = false;
    this.startX = 0;
    this.startScrollLeft = 0;
    this.looping = false;
    this.setWidth = 0;
    this.scrollEndTimer = null;

    this.prevButton = this.closest('div')?.querySelector('.reviews__arrow--prev');
    this.nextButton = this.closest('div')?.querySelector('.reviews__arrow--next');

    this.onPointerDown = this.onPointerDown.bind(this);
    this.onPointerMove = this.onPointerMove.bind(this);
    this.onPointerUp = this.onPointerUp.bind(this);
    this.onClickCapture = this.onClickCapture.bind(this);
    this.onScroll = this.onScroll.bind(this);
    this.onScrollEnd = this.onScrollEnd.bind(this);
    this.updateOverflow = this.updateOverflow.bind(this);
    this.onPrevClick = this.onPrevClick.bind(this);
    this.onNextClick = this.onNextClick.bind(this);

    this.addEventListener('pointerdown', this.onPointerDown);
    this.addEventListener('pointermove', this.onPointerMove);
    this.addEventListener('pointerup', this.onPointerUp);
    this.addEventListener('pointercancel', this.onPointerUp);
    this.addEventListener('click', this.onClickCapture, true);
    this.addEventListener('scroll', this.onScroll, { passive: true });
    this.addEventListener('scrollend', this.onScrollEnd);
    this.prevButton?.addEventListener('click', this.onPrevClick);
    this.nextButton?.addEventListener('click', this.onNextClick);

    this.resizeObserver = new ResizeObserver(this.updateOverflow);
    this.resizeObserver.observe(this);

    this.updateOverflow();
    this.centerOnMobile();
  }

  disconnectedCallback() {
    this.removeEventListener('pointerdown', this.onPointerDown);
    this.removeEventListener('pointermove', this.onPointerMove);
    this.removeEventListener('pointerup', this.onPointerUp);
    this.removeEventListener('pointercancel', this.onPointerUp);
    this.removeEventListener('click', this.onClickCapture, true);
    this.removeEventListener('scroll', this.onScroll);
    this.removeEventListener('scrollend', this.onScrollEnd);
    this.prevButton?.removeEventListener('click', this.onPrevClick);
    this.nextButton?.removeEventListener('click', this.onNextClick);
    this.resizeObserver?.disconnect();
    clearTimeout(this.scrollEndTimer);
  }

  get originalCards() {
    return Array.from(this.querySelectorAll('.reviews__card:not(.reviews__card--clone)'));
  }

  // Width of the original cards only (clones excluded), so the loop is only
  // turned on when the real reviews don't fit in the row.
  originalsOverflow() {
    const cards = this.originalCards;
    if (cards.length < 2) return false;
    const first = cards[0];
    const last = cards[cards.length - 1];
    const contentWidth = last.offsetLeft + last.offsetWidth - first.offsetLeft;
    const style = getComputedStyle(this);
    const available = this.clientWidth - parseFloat(style.paddingLeft) - parseFloat(style.paddingRight);
    return contentWidth > available + 1;
  }

  updateOverflow() {
    if (!this.looping && this.originalsOverflow()) {
      this.setupLoop();
    } else if (this.looping) {
      this.measureLoop();
      if (!this.originalsOverflow()) this.teardownLoop();
    }

    const hasOverflow = this.looping || this.scrollWidth > this.clientWidth + 1;
    this.classList.toggle('reviews__list--overflow', hasOverflow);

    if (!hasOverflow) {
      this.scrollLeft = 0;
    }

    this.updateNavigation();
  }

  // Infinite loop: one full copy of the reviews before and after the originals.
  // When scrolling settles inside a copy, jump back by one set width (invisible).
  setupLoop() {
    const cards = this.originalCards;
    if (cards.length < 2) return;

    const makeClone = (card) => {
      const clone = card.cloneNode(true);
      clone.classList.add('reviews__card--clone');
      clone.setAttribute('aria-hidden', 'true');
      clone.removeAttribute('data-shopify-editor-block');
      clone.querySelectorAll('a, button, [tabindex]').forEach((el) => el.setAttribute('tabindex', '-1'));
      return clone;
    };

    const before = document.createDocumentFragment();
    const after = document.createDocumentFragment();
    cards.forEach((card) => {
      before.appendChild(makeClone(card));
      after.appendChild(makeClone(card));
    });

    const currentOffset = this.scrollLeft;
    this.insertBefore(before, cards[0]);
    this.appendChild(after);
    this.looping = true;
    this.classList.add('reviews__list--overflow');
    this.measureLoop();
    this.jumpTo(currentOffset + this.setWidth);
  }

  teardownLoop() {
    this.querySelectorAll('.reviews__card--clone').forEach((clone) => clone.remove());
    this.looping = false;
    this.setWidth = 0;
    this.jumpTo(0);
  }

  measureLoop() {
    const firstOriginal = this.originalCards[0];
    const firstAfter = this.originalCards[this.originalCards.length - 1]?.nextElementSibling;
    if (!firstOriginal || !firstAfter) return;
    this.setWidth = firstAfter.offsetLeft - firstOriginal.offsetLeft;
  }

  jumpTo(left) {
    const previous = this.style.scrollBehavior;
    const previousSnap = this.style.scrollSnapType;
    this.style.scrollBehavior = 'auto';
    this.style.scrollSnapType = 'none';
    this.scrollLeft = left;
    // Restore on the next frame so snapping doesn't animate the jump.
    requestAnimationFrame(() => {
      this.style.scrollBehavior = previous;
      this.style.scrollSnapType = previousSnap;
    });
  }

  normalizeLoop() {
    if (!this.looping || this.isDown || !this.setWidth) return;
    const firstOriginal = this.originalCards[0];
    const start = firstOriginal.offsetLeft - parseFloat(getComputedStyle(this).paddingLeft);

    if (this.scrollLeft < start - this.setWidth / 2) {
      this.jumpTo(this.scrollLeft + this.setWidth);
    } else if (this.scrollLeft >= start + this.setWidth / 2) {
      this.jumpTo(this.scrollLeft - this.setWidth);
    }
  }

  onScroll() {
    this.updateNavigation();
    // Fallback for browsers without the scrollend event.
    clearTimeout(this.scrollEndTimer);
    this.scrollEndTimer = setTimeout(this.onScrollEnd, 150);
  }

  onScrollEnd() {
    clearTimeout(this.scrollEndTimer);
    this.normalizeLoop();
  }

  // Figma mobile starts with the middle review centred and its neighbours peeking in.
  centerOnMobile() {
    if (!window.matchMedia('(max-width: 749px)').matches) return;
    const cards = this.originalCards;
    if (cards.length < 3) return;
    const middle = cards[Math.floor(cards.length / 2)];
    const target = middle.offsetLeft - (this.clientWidth - middle.offsetWidth) / 2;
    this.jumpTo(Math.max(0, target));
  }

  updateNavigation() {
    if (this.looping) {
      this.prevButton?.removeAttribute('disabled');
      this.nextButton?.removeAttribute('disabled');
      return;
    }

    const hasOverflow = this.scrollWidth > this.clientWidth + 1;

    if (!hasOverflow) {
      this.prevButton?.setAttribute('disabled', '');
      this.nextButton?.setAttribute('disabled', '');
      return;
    }

    const atStart = this.scrollLeft <= 1;
    const atEnd = this.scrollLeft + this.clientWidth >= this.scrollWidth - 1;

    this.prevButton?.toggleAttribute('disabled', atStart);
    this.nextButton?.toggleAttribute('disabled', atEnd);
  }

  onPointerDown(event) {
    if (event.target.closest('.reviews__arrow-btn')) return;
    if (event.pointerType === 'touch') return;
    if (event.button !== 0) return;

    this.isDown = true;
    this.dragged = false;
    this.startX = event.clientX;
    this.startScrollLeft = this.scrollLeft;
    this.setPointerCapture(event.pointerId);
    this.classList.add('reviews__list--dragging');
  }

  onPointerMove(event) {
    if (!this.isDown) return;

    const delta = event.clientX - this.startX;

    if (Math.abs(delta) > 3) {
      this.dragged = true;
    }

    this.scrollLeft = this.startScrollLeft - delta;
  }

  onPointerUp(event) {
    if (!this.isDown) return;

    this.isDown = false;
    this.releasePointerCapture(event.pointerId);
    this.classList.remove('reviews__list--dragging');
    this.normalizeLoop();
  }

  onClickCapture(event) {
    if (this.dragged) {
      event.preventDefault();
      event.stopPropagation();
      this.dragged = false;
    }
  }

  getStep() {
    const card = this.querySelector('.reviews__card');
    if (!card) return this.clientWidth;
    const gap = parseFloat(getComputedStyle(this).columnGap) || 0;
    return card.offsetWidth + gap;
  }

  onPrevClick() {
    this.scrollBy({ left: -this.getStep(), behavior: 'smooth' });
  }

  onNextClick() {
    this.scrollBy({ left: this.getStep(), behavior: 'smooth' });
  }
}

customElements.define('reviews-list', ReviewsList);
